import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useEffect, useRef } from 'react'

import { FileDownload, Folder, OverflowMenu } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'

interface DraftRowMenuProps {
  isExporting: boolean
  onDownload: () => void
  /** Saves a copy into the matter's files. Absent on a draft this user did not create, and where the matter cannot receive one. */
  onSaveToMatterFiles?: () => void
}

/**
 * DraftRowMenu
 *
 * The actions available on a Drafts list row. The trigger doubles as the row's export progress,
 * since the list has nowhere else to show it.
 */
export function DraftRowMenu({ isExporting, onDownload, onSaveToMatterFiles }: DraftRowMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Exporting disables the trigger, which drops focus, so put it back when the export ends unless
  // the user has moved on themselves.
  const wasExportingRef = useRef(isExporting)
  useEffect(() => {
    const wasExporting = wasExportingRef.current
    wasExportingRef.current = isExporting
    if (!wasExporting || isExporting || document.activeElement !== document.body) return
    triggerRef.current?.focus()
  }, [isExporting])

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild disabled={isExporting}>
        <IconButton
          ref={triggerRef}
          icon={<OverflowMenu />}
          variant="plain"
          color="neutral"
          size="sm"
          loading={isExporting}
          aria-label={isExporting ? 'Exporting draft' : 'Draft actions'}
        />
      </DropdownMenu.Trigger>

      <MenuContent side="bottom" align="end">
        <MenuItemGroup>
          <MenuItem label="Download" icon={FileDownload} variant="neutral" onSelect={onDownload} />
          {onSaveToMatterFiles && <MenuItem label="Save copy to matter files" icon={Folder} variant="neutral" onSelect={onSaveToMatterFiles} />}
        </MenuItemGroup>
      </MenuContent>
    </DropdownMenu.Root>
  )
}
